import { Box, Center, Input, Select, Text, Textarea } from "@chakra-ui/react";
import GaraazAlertDialog from "./GaraazAlertDialog";
import React, { useState } from "react";
import garaazToast from "./GaraazHocToast";
import {
  ReceivePaymentModalProps,
  CollectedBy,
  PaymentModeOption,
  defaultPaymentModes,
  InvoiceType,
  PaymentMode,
} from "../utils/types";

const mergePaymentModes = (
  defaults: PaymentModeOption[],
  overrides: (Partial<PaymentModeOption> & { value: string })[] = []
): PaymentModeOption[] => {
  if (!overrides.length) return defaults;

  const map = new Map<string, PaymentModeOption>(
    defaults.map((m) => [m.value, { ...m }])
  );

  for (const ov of overrides) {
    const existing = map.get(ov.value);

    if (existing) {
      map.set(ov.value, { ...existing, ...ov });
    } else {
      map.set(ov.value, {
        ...ov,
        value: ov.value,
        label: ov.label ?? ov.value,
      } as PaymentModeOption);
    }
  }

  return Array.from(map.values());
};

const ReceivePaymentModal: React.FC<ReceivePaymentModalProps> = ({
  auth,
  isOpen,
  onClose,
  collector,
  orderDetails,
  onCollectPayment,
  paymentModes = [],
  title = "Receive Payment",
  selectPlaceholder = "Select Mode of Payment",
  maxNotesLength = 500,
  onPaymentSuccess,
  notification,
}) => {
  const [paymentMode, setPaymentMode] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const effectivePaymentModes = mergePaymentModes(
    defaultPaymentModes,
    paymentModes
  );
  const [submitting, setSubmitting] = useState(false);

  const handleCollectPaymentSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    if (!paymentMode) {
      notification({
        description: "Please select a payment mode",
        status: "error",
      });
      setSubmitting(false);
      return;
    }

    try {
      const payload: any = {
        auth,
        orderNo: orderDetails.orderNo,
        invoiceNo: orderDetails?.invoiceNo,
        mode: paymentMode,
        collectedBy: collector.collectedBy ?? CollectedBy.SUPPLIER,
        collectorId: collector.id,
        notes: notes || "",
      };

      if (
        orderDetails?.invoiceType &&
        Object.values(InvoiceType).includes(orderDetails.invoiceType)
      ) {
        payload.invoiceType = orderDetails.invoiceType;
      }

      await onCollectPayment(payload);

      notification({
        description: "Payment collected successfully",
        status: "success",
      });
      onPaymentSuccess?.();
      onClose();
    } catch (error: any) {
      const fallback =
        typeof error?.message === "string"
          ? error.message
          : "Failed to collect payment";
      notification({
        description: error?.response?.data?.message || fallback,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fullName = [collector?.firstName, collector?.lastName]
    ?.filter(Boolean)
    .join(" ");

  return (
    <GaraazAlertDialog
      isOpen={isOpen}
      headerText={null}
      maxWidth="400px"
      bodyText={
        <Box p={0} m={0}>
          <Center mb={2} mt={-2}>
            <Box
              bg="#DBEAFE"
              borderRadius="full"
              p={2.5}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="56px"
              height="56px"
            >
              <Text fontSize="32px" color="#2563EB" fontWeight="bold">
                ₹
              </Text>
            </Box>
          </Center>

          <Text
            fontWeight="bold"
            fontSize="xl"
            textAlign="center"
            mb={1}
            mt={2}
          >
            {title}
          </Text>

          <Box p={2} mb={2}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Text fontWeight="semibold" fontSize="sm" color="#374151">
                Collector Name
              </Text>
            </Box>
            <Input
              value={fullName}
              isReadOnly
              variant="filled"
              width="100%"
              padding="12px 16px"
              border="2px solid #DBEAFE"
              borderRadius="8px"
              backgroundColor="#EFF6FF"
              color="#374151"
              cursor="not-allowed"
              fontSize="14px"
              fontWeight="500"
              height="48px"
            />
            <Box display="flex" alignItems="center" gap={1} m={1}>
              <span style={{ fontSize: "14px", marginBottom: "1px" }}>⚠️</span>
              <Text fontSize="sm" color="#92400E" fontWeight="medium">
                It will be recorded.
              </Text>
            </Box>
          </Box>

          <Box p={2} mb={2}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Text fontSize="18px" color="#10B981" fontWeight="bold">
                ₹
              </Text>
              <Text fontWeight="semibold" fontSize="sm" color="#374151">
                Mode of Payment
              </Text>
            </Box>
            <Select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              width="100%"
              border="2px solid"
              borderColor="#D1FAE5"
              borderRadius="8px"
              backgroundColor="#ECFDF5"
              color="#374151"
              cursor="pointer"
              fontSize="14px"
              fontWeight="500"
              outline="none"
              height="48px"
              sx={{ paddingTop: "0px" }}
            >
              <option value="">{selectPlaceholder}</option>
              {effectivePaymentModes
                .filter((opt) => !opt.hidden)
                .map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                  >
                    {opt.prefix ? `${opt.prefix} ` : ""}
                    {opt.label}
                  </option>
                ))}
            </Select>
            {/* Advance payment vs invoice amount info — commented out
            {paymentMode === PaymentMode.ADVANCE_PAYMENT && (
              <Box mt={2}>
                <Text fontSize="sm">Invoice Amount : ₹{orderDetails.invoiceAmount}</Text>
                <Text fontSize="sm">Customer Advance Payment : ₹{orderDetails.customerAdvancePayment}</Text>
                {Number(orderDetails.customerAdvancePayment) < Number(orderDetails.invoiceAmount) && (
                  <Text fontSize="sm" color={"red"}>Customer Advance Payment is less than Invoice Amount</Text>
                )}
              </Box>
            )}
            */}
          </Box>

          {/* Notes */}
          <Box p={2}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Text fontWeight="semibold" fontSize="sm" color="#374151">
                Notes
              </Text>
            </Box>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter any additional notes (optional)"
              rows={4}
              width="100%"
              padding="12px 16px"
              border="2px solid #E9D5FF"
              borderRadius="8px"
              backgroundColor="#FAF5FF"
              color="#374151"
              resize="none"
              fontSize="14px"
              outline="none"
              maxLength={maxNotesLength}
            />
            <Text
              fontSize="xs"
              color="#8B5CF6"
              fontWeight="medium"
              textAlign={"end"}
            >
              {notes.length}/{maxNotesLength} characters
            </Text>
          </Box>
        </Box>
      }
      showCancelButton={true}
      submitBtnText={"Submit"}
      isSubmitButtonDisabled={!paymentMode || submitting /* || (paymentMode === PaymentMode.ADVANCE_PAYMENT && Number(orderDetails.customerAdvancePayment) < Number(orderDetails.invoiceAmount)) */}
      isLoading={submitting}
      onSubmitButtonClick={handleCollectPaymentSubmit}
      onClose={onClose}
      branchName={""}
    />
  );
};

export default garaazToast(ReceivePaymentModal);
